import { useState, useEffect, useMemo, useCallback } from "react";
import { supabaseAdmin as supabase } from "@/lib/supabaseAdmin";
import { useAdmin } from "@/hooks/useAdmin";

interface VerificationProfile {
  id: string;
  name: string | null;
  username: string | null;
  phone: string | null;
  full_name: string | null;
  bank_name: string | null;
  branch_code: string | null;
  bank_account: string | null;
  account_holder: string | null;
  verification_status: string | null;
  rejection_reason: string | null;
  id_card_front: string | null;
  id_card_back: string | null;
  created_at: string | null;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}`;
}

function getStatusLabel(status: string | null): { text: string; style: string } {
  switch (status) {
    case "approved":
      return { text: "已認證", style: "bg-emerald-50 text-emerald-600 border-emerald-200" };
    case "pending":
      return { text: "審核中", style: "bg-amber-50 text-amber-600 border-amber-200" };
    case "rejected":
      return { text: "已拒絕", style: "bg-red-50 text-red-500 border-red-200" };
    default:
      return { text: "未認證", style: "bg-stone-50 text-stone-500 border-stone-200" };
  }
}

export default function AdminVerifications() {
  const { updateVerificationStatus } = useAdmin();
  const [profiles, setProfiles] = useState<VerificationProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "pending" | "approved" | "rejected">("pending");
  const [reviewing, setReviewing] = useState<VerificationProfile | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: fetchError } = await supabase
        .from("profiles")
        .select(
          "id, name, username, phone, full_name, bank_name, branch_code, bank_account, account_holder, verification_status, rejection_reason, id_card_front, id_card_back, created_at"
        )
        .order("created_at", { ascending: false })
        .limit(5000);

      if (fetchError) {
        setError(`讀取認證資料失敗：${fetchError.message}`);
      } else {
        setProfiles(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "資料載入失敗");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Realtime subscription: auto-refresh when profiles change
  useEffect(() => {
    const channel = supabase
      .channel("profiles-verification-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles" },
        () => {
          fetchProfiles();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchProfiles]);

  const filtered = useMemo(() => {
    if (activeFilter === "all") return profiles;
    return profiles.filter((p) => {
      if (activeFilter === "pending") return p.verification_status === "pending";
      if (activeFilter === "approved") return p.verification_status === "approved";
      if (activeFilter === "rejected") return p.verification_status === "rejected";
      return true;
    });
  }, [profiles, activeFilter]);

  const counts = useMemo(() => {
    const pending = profiles.filter((p) => p.verification_status === "pending").length;
    const approved = profiles.filter((p) => p.verification_status === "approved").length;
    const rejected = profiles.filter((p) => p.verification_status === "rejected").length;
    const unverified = profiles.filter((p) => !p.verification_status || p.verification_status === "").length;
    return { pending, approved, rejected, unverified, total: profiles.length };
  }, [profiles]);

  const handleApprove = async (userId: string) => {
    setProcessingId(userId);
    try {
      await updateVerificationStatus(userId, "approved");
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === userId
            ? { ...p, verification_status: "approved", rejection_reason: null }
            : p
        )
      );
      setActionMsg("實名認證已核准通過");
      setReviewing(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "核准失敗";
      setActionMsg(`核准失敗：${msg}`);
    } finally {
      setProcessingId(null);
      setTimeout(() => setActionMsg(""), 3000);
    }
  };

  const handleReject = async (userId: string, reason: string) => {
    if (!reason.trim()) {
      setActionMsg("請輸入駁回原因");
      setTimeout(() => setActionMsg(""), 3000);
      return;
    }
    setProcessingId(userId);
    try {
      await updateVerificationStatus(userId, "rejected", reason.trim());
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === userId
            ? { ...p, verification_status: "rejected", rejection_reason: reason.trim() }
            : p
        )
      );
      setActionMsg("已駁回認證申請");
      setReviewing(null);
      setShowRejectInput(false);
      setRejectReason("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "駁回失敗";
      setActionMsg(`駁回失敗：${msg}`);
    } finally {
      setProcessingId(null);
      setTimeout(() => setActionMsg(""), 3000);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <i className="ri-time-line text-amber-600 text-lg" />
            </div>
            <span className="font-body text-[11px] text-navy-400">待審核</span>
          </div>
          <p className="font-display text-2xl font-bold text-navy-900">{loading ? "—" : counts.pending}</p>
          <p className="font-body text-xs text-navy-400 mt-1">筆認證待審核</p>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <i className="ri-shield-check-line text-emerald-600 text-lg" />
            </div>
            <span className="font-body text-[11px] text-navy-400">已認證</span>
          </div>
          <p className="font-display text-2xl font-bold text-navy-900">{loading ? "—" : counts.approved}</p>
          <p className="font-body text-xs text-navy-400 mt-1">筆已通過實名</p>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
              <i className="ri-close-circle-line text-red-500 text-lg" />
            </div>
            <span className="font-body text-[11px] text-navy-400">已駁回</span>
          </div>
          <p className="font-display text-2xl font-bold text-navy-900">{loading ? "—" : counts.rejected}</p>
          <p className="font-body text-xs text-navy-400 mt-1">筆認證被拒絕</p>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-navy-50 flex items-center justify-center">
              <i className="ri-user-line text-navy-600 text-lg" />
            </div>
            <span className="font-body text-[11px] text-navy-400">會員總數</span>
          </div>
          <p className="font-display text-2xl font-bold text-navy-900">{loading ? "—" : counts.total}</p>
          <p className="font-body text-xs text-navy-400 mt-1">位註冊會員</p>
        </div>
      </div>

      {actionMsg && (
        <div
          className={`font-body text-sm px-4 py-2.5 rounded-lg ${
            actionMsg.includes("失敗") || actionMsg.includes("請輸入")
              ? "bg-red-50 text-red-600 border border-red-200"
              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}
        >
          {actionMsg}
        </div>
      )}

      {error && (
        <div className="font-body text-sm px-4 py-2.5 rounded-lg bg-red-50 text-red-600 border border-red-200">
          {error}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex items-center gap-2 bg-white rounded-lg border border-navy-100 p-1 w-fit">
        {[
          { key: "pending", label: "待審核", count: counts.pending },
          { key: "approved", label: "已認證", count: counts.approved },
          { key: "rejected", label: "已駁回", count: counts.rejected },
          { key: "all", label: "全部", count: counts.total },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key as typeof activeFilter)}
            className={`flex items-center gap-1.5 font-body text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer ${
              activeFilter === tab.key
                ? "bg-navy-700 text-white"
                : "text-navy-500 hover:text-navy-700 hover:bg-navy-50"
            }`}
          >
            {tab.label}
            <span
              className={`font-body text-[10px] px-1 py-0.5 rounded-full min-w-[18px] text-center ${
                activeFilter === tab.key ? "bg-white/20 text-white" : "bg-navy-50 text-navy-500"
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-100 bg-navy-50/50">
                {["用戶帳號", "真實姓名", "銀行帳號", "提交時間", "狀態", "操作"].map((h) => (
                  <th
                    key={h}
                    className="font-body text-[11px] font-semibold text-navy-600 text-left px-4 py-3 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-8 h-8 rounded-full border-2 border-navy-200 border-t-navy-700 animate-spin" />
                      <p className="font-body text-sm text-navy-400">載入認證資料中...</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-navy-50 flex items-center justify-center">
                        <i className="ri-file-list-3-line text-navy-300 text-xl" />
                      </div>
                      <p className="font-body text-sm text-navy-400">
                        {activeFilter === "pending"
                          ? "目前沒有待審核的認證申請"
                          : activeFilter === "approved"
                            ? "目前沒有已認證的會員"
                            : activeFilter === "rejected"
                              ? "目前沒有已駁回的認證申請"
                              : "暫無認證資料"}
                      </p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((p) => {
                  const status = getStatusLabel(p.verification_status);
                  const isProcessing = processingId === p.id;
                  return (
                    <tr key={p.id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-navy-100 flex items-center justify-center shrink-0">
                            <span className="font-body text-xs font-bold text-navy-600">
                              {(p.name || "未").slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <p className="font-body text-sm font-medium text-navy-900">
                              {p.name || "未命名"}
                            </p>
                            <p className="font-body text-[10px] text-navy-400 font-mono">
                              {p.username || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-body text-sm text-navy-700">
                        {p.full_name || "—"}
                      </td>
                      <td className="px-4 py-3 font-body text-xs text-navy-500 font-mono">
                        {p.bank_account || "—"}
                      </td>
                      <td className="px-4 py-3 font-body text-xs text-navy-500">
                        {formatDate(p.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${status.style}`}
                        >
                          {status.text}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {p.verification_status === "pending" && !isProcessing && (
                          <button
                            onClick={() => {
                              setReviewing(p);
                              setShowRejectInput(false);
                              setRejectReason("");
                            }}
                            className="font-body text-xs font-semibold px-3 py-1.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 cursor-pointer whitespace-nowrap"
                          >
                            查看並審核
                          </button>
                        )}
                        {p.verification_status !== "pending" && !isProcessing && (
                          <button
                            onClick={() => {
                              setReviewing(p);
                              setShowRejectInput(false);
                              setRejectReason("");
                            }}
                            className="font-body text-xs font-medium px-3 py-1.5 rounded-lg border border-navy-200 text-navy-500 hover:bg-navy-50 hover:text-navy-700 cursor-pointer whitespace-nowrap"
                          >
                            查看詳情
                          </button>
                        )}
                        {isProcessing && (
                          <span className="font-body text-xs text-navy-400 flex items-center gap-1">
                            <i className="ri-loader-4-line animate-spin" />
                            處理中...
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Review Modal */}
      {reviewing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setReviewing(null)} />
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-navy-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="font-display text-base font-bold text-navy-900">實名認證審核</h3>
                <p className="font-body text-xs text-navy-400 mt-0.5">
                  會員：{reviewing.name || reviewing.username || "未命名"}
                </p>
              </div>
              <button
                onClick={() => setReviewing(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-6 py-5 flex flex-col gap-5">
              {/* Bank Info */}
              <div>
                <h4 className="font-body text-xs font-semibold text-navy-400 uppercase tracking-wider mb-3">
                  銀行帳戶資訊
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-navy-50/50 rounded-lg px-4 py-3">
                    <p className="font-body text-[10px] text-navy-400 mb-0.5">真實姓名</p>
                    <p className="font-body text-sm font-medium text-navy-900">
                      {reviewing.full_name || "—"}
                    </p>
                  </div>
                  <div className="bg-navy-50/50 rounded-lg px-4 py-3">
                    <p className="font-body text-[10px] text-navy-400 mb-0.5">手機號碼</p>
                    <p className="font-body text-sm font-medium text-navy-900">
                      {reviewing.phone || "—"}
                    </p>
                  </div>
                  <div className="bg-navy-50/50 rounded-lg px-4 py-3">
                    <p className="font-body text-[10px] text-navy-400 mb-0.5">銀行名稱</p>
                    <p className="font-body text-sm font-medium text-navy-900">
                      {reviewing.bank_name || "—"}
                    </p>
                  </div>
                  <div className="bg-navy-50/50 rounded-lg px-4 py-3">
                    <p className="font-body text-[10px] text-navy-400 mb-0.5">分行代碼</p>
                    <p className="font-body text-sm font-medium text-navy-900">
                      {reviewing.branch_code || "—"}
                    </p>
                  </div>
                  <div className="bg-navy-50/50 rounded-lg px-4 py-3">
                    <p className="font-body text-[10px] text-navy-400 mb-0.5">銀行帳號</p>
                    <p className="font-body text-sm font-medium text-navy-900 font-mono">
                      {reviewing.bank_account || "—"}
                    </p>
                  </div>
                  <div className="bg-navy-50/50 rounded-lg px-4 py-3">
                    <p className="font-body text-[10px] text-navy-400 mb-0.5">戶名</p>
                    <p className="font-body text-sm font-medium text-navy-900">
                      {reviewing.account_holder || "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* ID Card Images */}
              <div>
                <h4 className="font-body text-xs font-semibold text-navy-400 uppercase tracking-wider mb-3">
                  身分證件上傳
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: "身分證正面", url: reviewing.id_card_front },
                    { label: "身分證反面", url: reviewing.id_card_back },
                  ].map((img) => (
                    <div
                      key={img.label}
                      className="rounded-lg border border-navy-100 overflow-hidden bg-navy-50/30"
                    >
                      <p className="font-body text-[10px] text-navy-500 px-3 py-2 bg-navy-50/50 border-b border-navy-100">
                        {img.label}
                      </p>
                      <div className="p-3">
                        {img.url ? (
                          <button
                            onClick={() => setImagePreview(img.url)}
                            className="w-full aspect-[4/3] rounded-md overflow-hidden bg-navy-100 cursor-pointer block"
                          >
                            <img
                              src={img.url}
                              alt={img.label}
                              className="w-full h-full object-contain"
                              loading="lazy"
                            />
                          </button>
                        ) : (
                          <div className="w-full aspect-[4/3] rounded-md bg-navy-50 flex flex-col items-center justify-center gap-1">
                            <i className="ri-image-line text-navy-300 text-xl" />
                            <span className="font-body text-[10px] text-navy-300">未上傳</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rejection reason display for already rejected */}
              {reviewing.verification_status === "rejected" && reviewing.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <p className="font-body text-[10px] text-red-500 mb-1">駁回原因</p>
                  <p className="font-body text-sm text-red-700">{reviewing.rejection_reason}</p>
                </div>
              )}

              {/* Reject input */}
              {reviewing.verification_status === "pending" && showRejectInput && (
                <div className="flex flex-col gap-2">
                  <label className="font-body text-xs font-medium text-navy-600">
                    駁回原因（會員將看到此訊息）
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="例如：照片不清晰、資料不符、身分證過期..."
                    maxLength={200}
                    rows={3}
                    className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white resize-none"
                  />
                  <p className="font-body text-[10px] text-navy-400 text-right">
                    {rejectReason.length}/200
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-navy-100 px-6 py-4 flex items-center justify-end gap-2 z-10">
              {reviewing.verification_status === "pending" && (
                <>
                  {!showRejectInput ? (
                    <>
                      <button
                        onClick={() => setShowRejectInput(true)}
                        disabled={processingId === reviewing.id}
                        className="font-body text-sm font-medium px-4 py-2 rounded-lg border border-red-300 text-red-500 hover:bg-red-50 cursor-pointer whitespace-nowrap"
                      >
                        駁回
                      </button>
                      <button
                        onClick={() => handleApprove(reviewing.id)}
                        disabled={processingId === reviewing.id}
                        className={`font-body text-sm font-medium px-4 py-2 rounded-lg text-white cursor-pointer whitespace-nowrap ${
                          processingId === reviewing.id
                            ? "bg-emerald-300 cursor-not-allowed"
                            : "bg-emerald-500 hover:bg-emerald-600"
                        }`}
                      >
                        {processingId === reviewing.id ? (
                          <span className="flex items-center gap-1">
                            <i className="ri-loader-4-line animate-spin" />
                            處理中...
                          </span>
                        ) : (
                          "通過"
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setShowRejectInput(false);
                          setRejectReason("");
                        }}
                        disabled={processingId === reviewing.id}
                        className="font-body text-sm font-medium px-4 py-2 rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50 cursor-pointer whitespace-nowrap"
                      >
                        取消
                      </button>
                      <button
                        onClick={() => handleReject(reviewing.id, rejectReason)}
                        disabled={processingId === reviewing.id}
                        className={`font-body text-sm font-medium px-4 py-2 rounded-lg text-white cursor-pointer whitespace-nowrap ${
                          processingId === reviewing.id
                            ? "bg-red-300 cursor-not-allowed"
                            : "bg-red-500 hover:bg-red-600"
                        }`}
                      >
                        {processingId === reviewing.id ? (
                          <span className="flex items-center gap-1">
                            <i className="ri-loader-4-line animate-spin" />
                            處理中...
                          </span>
                        ) : (
                          "確認駁回"
                        )}
                      </button>
                    </>
                  )}
                </>
              )}

              {reviewing.verification_status !== "pending" && (
                <button
                  onClick={() => setReviewing(null)}
                  className="font-body text-sm font-medium px-4 py-2 rounded-lg bg-navy-700 text-white hover:bg-navy-800 cursor-pointer whitespace-nowrap"
                >
                  關閉
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreview && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setImagePreview(null)} />
          <div className="relative bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-navy-100">
              <p className="font-body text-sm font-medium text-navy-900">圖片預覽</p>
              <button
                onClick={() => setImagePreview(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-navy-50/30">
              <img
                src={imagePreview}
                alt="ID Card Preview"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}